import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, User, Clock, Film, Sparkles, ExternalLink, Star, UserPlus, UserMinus, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import { getPublicProfile, getFollowStatus, followUser, unfollowUser } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import BadgeCase from "../components/BadgeCase";

// ---------------------------------------------------------------------------
// PublicProfile — read-only /u/{handle}. No auth required, no client identity
// leaked (uses the plain axios helper, not one that forwards creator_id).
// ---------------------------------------------------------------------------

export default function PublicProfile() {
  const { handle } = useParams();
  const { user: me } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [follow, setFollow] = useState(null); // { is_following, followers, following, is_self }
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPublicProfile(handle)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(status === 404 ? "not-found" : "generic");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [handle]);

  // Follow status is fetched independently — it's public-safe and needs the
  // signed-in cookie to reveal is_following/is_self correctly.
  useEffect(() => {
    let cancelled = false;
    getFollowStatus(handle)
      .then((s) => { if (!cancelled) setFollow(s); })
      .catch(() => { if (!cancelled) setFollow(null); });
    return () => { cancelled = true; };
  }, [handle, me?.user_id]);

  const toggleFollow = async () => {
    if (!me) {
      toast.error("Sign in to follow @" + handle);
      return;
    }
    if (!follow || follow.is_self) return;
    setFollowBusy(true);
    try {
      if (follow.is_following) {
        await unfollowUser(handle);
        setFollow({ ...follow, is_following: false, followers: Math.max(0, (follow.followers || 1) - 1) });
        toast.success(`Unfollowed @${handle}`);
      } else {
        await followUser(handle);
        setFollow({ ...follow, is_following: true, followers: (follow.followers || 0) + 1 });
        toast.success(`Following @${handle}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update follow");
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  if (error) return <ProfileNotFound handle={handle} kind={error} />;
  if (!data) return null;

  const { user, stats, persona, entries, badges, badge_catalog } = data;
  const canCompare = me && me.handle && follow && !follow.is_self;
  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
      <ProfileHeader
        user={user}
        persona={persona}
        follow={follow}
        onToggleFollow={toggleFollow}
        followBusy={followBusy}
        canCompare={canCompare}
        meHandle={me?.handle}
      />
      <StatsRow stats={stats} />
      <GridSection entries={entries} />
      <BadgeCase catalog={badge_catalog} earned={badges} testid="profile-badges" />
      <FooterCTA />
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProfileHeader({ user, persona, follow, onToggleFollow, followBusy, canCompare, meHandle }) {
  const initial = (user.display_name || user.handle || "?").charAt(0).toUpperCase();
  return (
    <div data-testid="profile-header" className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
      <div className="flex items-start gap-5">
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.display_name}
            crossOrigin="anonymous"
            className="h-24 w-24 border border-white/15 object-cover"
            data-testid="profile-avatar"
          />
        ) : (
          <div
            data-testid="profile-avatar-fallback"
            className="flex h-24 w-24 items-center justify-center border border-white/15 bg-[#111] font-display text-4xl uppercase text-white"
          >
            {initial}
          </div>
        )}
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Public Profile</p>
          <h1
            data-testid="profile-display-name"
            className="mt-2 font-display text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl"
          >
            {user.display_name}
          </h1>
          <p data-testid="profile-handle" className="mt-2 font-mono text-sm text-neutral-500">
            @{user.handle}
          </p>
          {follow && (
            <p data-testid="profile-follow-counts" className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
              {follow.followers} follower{follow.followers === 1 ? "" : "s"} · {follow.following} following
            </p>
          )}
          {persona?.personality_type && (
            <Link
              to={persona.share_id ? `/p/${persona.share_id}` : "#"}
              data-testid="profile-persona-badge"
              className="mt-4 inline-flex items-center gap-2 border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-white transition-colors hover:bg-[#FF3B30]/25"
            >
              <Sparkles className="h-3 w-3" />
              {persona.personality_type}
              {persona.share_id && <ExternalLink className="h-3 w-3 opacity-70" />}
            </Link>
          )}
        </div>
      </div>

      {follow && !follow.is_self && (
        <div className="flex flex-wrap gap-2 md:justify-end">
          <button
            onClick={onToggleFollow}
            disabled={followBusy}
            data-testid={follow.is_following ? "unfollow-btn" : "follow-btn"}
            className={`inline-flex items-center gap-2 border px-4 py-2 font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-50 ${
              follow.is_following
                ? "border-white/20 bg-transparent text-white hover:border-[#FF3B30] hover:text-[#FF3B30]"
                : "border-white/20 bg-[#FF3B30] text-white hover:bg-white hover:text-black"
            }`}
          >
            {follow.is_following ? <UserMinus className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
            {follow.is_following ? "Following" : "Follow"}
          </button>
          {canCompare && (
            <Link
              to={`/compare/${meHandle}/${user.handle}`}
              data-testid="compare-cta"
              className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <GitCompareArrows className="h-3.5 w-3.5" /> Compare Personas
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function StatsRow({ stats }) {
  const topGenres = stats.top_genres || [];
  return (
    <div data-testid="profile-stats" className="mb-10 grid grid-cols-1 gap-4 md:grid-cols-3">
      <StatCard icon={<Film className="h-4 w-4" />} label="Total Anime" value={stats.total_anime} testid="stat-total" />
      <StatCard
        icon={<Clock className="h-4 w-4" />}
        label="Hours Watched"
        value={stats.total_watch_time_hours}
        testid="stat-hours"
      />
      <div
        data-testid="stat-top-genres"
        className="border border-white/10 bg-white/[0.02] p-4"
      >
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          <Sparkles className="h-3 w-3" /> Top Genres
        </p>
        {topGenres.length === 0 ? (
          <p className="mt-3 font-mono text-xs uppercase tracking-widest text-neutral-600">No genres yet</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2">
            {topGenres.map((g, i) => (
              <span
                key={g}
                className={`border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  i === 0
                    ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                    : "border-white/15 text-neutral-300"
                }`}
              >
                {g}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-white/10 bg-white/[0.02] p-4">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
        {icon} {label}
      </p>
      <p className="mt-3 font-display text-5xl uppercase leading-none tracking-tight text-white">
        {value ?? 0}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function GridSection({ entries }) {
  if (!entries || entries.length === 0) {
    return (
      <div
        data-testid="profile-empty"
        className="mb-16 border border-dashed border-white/15 py-20 text-center"
      >
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-neutral-600">// Empty Shelf</p>
        <h2 className="mt-4 font-display text-4xl uppercase text-white">
          Nothing shared yet
        </h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
          This user hasn&apos;t added any completed or watching anime to their catalogue yet.
        </p>
      </div>
    );
  }
  return (
    <section className="mb-16">
      <div className="mb-4 flex items-baseline justify-between border-b border-white/10 pb-3">
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Catalogue</h2>
        <span className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          {entries.length} shown
        </span>
      </div>
      <div
        data-testid="profile-grid"
        className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
      >
        {entries.map((e) => (
          <PosterCard key={e.mal_id} entry={e} />
        ))}
      </div>
    </section>
  );
}

function PosterCard({ entry }) {
  const rating = entry.user_rating ?? entry.mal_score;
  return (
    <div
      data-testid={`profile-card-${entry.mal_id}`}
      className="group relative border border-white/10 bg-[#0a0a0a] transition-colors hover:border-[#FF3B30]"
    >
      <div className="aspect-[2/3] w-full overflow-hidden bg-[#111]">
        {entry.image_url ? (
          <img
            src={entry.image_url}
            alt={entry.title}
            loading="lazy"
            crossOrigin="anonymous"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-[10px] uppercase tracking-widest text-neutral-600">
            No image
          </div>
        )}
      </div>
      <div className="absolute right-2 top-2 flex items-center gap-1 border border-white/15 bg-black/80 px-1.5 py-0.5 font-mono text-[10px] text-white">
        <Star className="h-3 w-3 fill-[#FF3B30] text-[#FF3B30]" /> {rating?.toFixed?.(1) ?? "—"}
      </div>
      <div className="absolute left-2 top-2 border border-white/15 bg-black/80 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest">
        <span className={entry.status === "watching" ? "text-[#FF3B30]" : "text-emerald-400"}>
          {entry.status === "watching" ? "Watching" : "Completed"}
        </span>
      </div>
      <div className="p-3">
        <p className="line-clamp-2 min-h-[2.5rem] font-display text-base uppercase leading-tight tracking-wide text-white">
          {entry.title}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          {entry.episodes ? `${entry.episodes} ep` : entry.type || "—"}
          {entry.year ? ` · ${entry.year}` : ""}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function FooterCTA() {
  return (
    <div
      data-testid="profile-footer-cta"
      className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 md:flex-row"
    >
      <div className="flex items-center gap-3">
        <Logo variant="dark" className="h-8" />
        <p className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
          Otaku Index · Track · Analyze · Share
        </p>
      </div>
      <Link
        to="/"
        data-testid="profile-track-yours-cta"
        className="inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black"
      >
        Track your own
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ProfileNotFound({ handle, kind }) {
  return (
    <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10 text-center" data-testid="profile-not-found">
      <User className="mx-auto h-10 w-10 text-neutral-600" />
      <h1 className="mt-4 font-display text-5xl uppercase text-white md:text-7xl">
        {kind === "not-found" ? "Profile private" : "Couldn't load"}
      </h1>
      <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400">
        {kind === "not-found"
          ? <>@{handle} either doesn&apos;t exist or hasn&apos;t made their catalogue public yet.</>
          : "Something went wrong loading this profile. Try again in a moment."}
      </p>
      <Link
        to="/"
        className="mt-6 inline-block border border-white/20 bg-[#FF3B30] px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
      >
        Back to home
      </Link>
    </div>
  );
}
